/* AS-03 · CONTACT SUPPORT
   States what is attached and what never is, and surfaces reasoned access to
   the merchant: a motive on record, a bounded duration, and a trace they can
   see in Paramètres. */
import { useState } from 'react'
import { Button, Card, Field, KV, Note, PageHeader, Textarea, useToast } from '@/ui'
import { useCampaigns, useLocations, useSupports } from '@/lib/api'
import { allCampaigns, getPlan, mergeSupports, principalOf, servedBy } from '@/lib/campaign'
import { planOffer } from '@/lib/plans'
import { readiness } from '@/lib/readiness'
import { allowedLocations } from '@/lib/scope'

/* v3.3 WF-046 (pack tests-aide) · « Préparer un diagnostic »: local, nothing sent. The section's name and the configuration
   states this browser can read — the establishments, the offer, the principale, each support's readiness states (lib/readiness,
   the five states) — never a gift code, a personal code or a customer's address. */
function Diagnostic() {
  const toast = useToast()
  const [text, setText] = useState<string | null>(null)
  const { data: locations } = useLocations()
  const { data: apiCampaigns } = useCampaigns()
  const { data: apiSupports } = useSupports()
  const prepare = () => {
    const locs = allowedLocations(locations ?? [])
    const names: Record<string, string> = Object.fromEntries(locs.map(l => [l.id, l.name]))
    const lite = allCampaigns(apiCampaigns, names)
    const supports = mergeSupports(apiSupports).filter(s => s.kind !== 'personnel' && locs.some(l => l.id === s.locationId))
    const lines = [
      'Section : Aide › Écrire au support',
      `Établissement${locs.length > 1 ? 's' : ''} : ${locs.map(l => l.name).join(', ') || '—'}`,
      `Offre : ${planOffer(getPlan())}`,
      ...locs.map(l => { const p = principalOf(l.id, lite); return `Campagne principale · ${l.name} : ${p ? `« ${p.name} » (${p.status === 'active' ? 'en ligne' : 'programmée'})` : 'aucune'}` }),
      `Campagnes : ${lite.filter(c => c.status === 'active').length} en ligne · ${lite.filter(c => c.status === 'paused').length} en pause · ${lite.filter(c => c.status === 'draft').length} brouillon${lite.filter(c => c.status === 'draft').length > 1 ? 's' : ''}`,
      `Supports : ${supports.length}`,
      ...supports.map(s => { const st = readiness(s, servedBy(s, lite).current); const todo = st.filter(x => !x.done).map(x => x.label); return `  · ${s.name} — ${todo.length ? todo.join(' · ') : 'prêt (cinq états)'}` }),
    ]
    setText(lines.join('\n'))
  }
  return (
    <div data-delta="migration v3.3 WF-046 — préparer un diagnostic, local" data-testid="diagnostic" style={{ display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="quiet" size="sm" onClick={prepare}>Préparer un diagnostic</Button>
        <span className="f-help">Le diagnostic contient le nom de la section et les états de configuration. Il n’inclut ni code cadeau, ni code personnel, ni adresse client.</span>
      </div>
      {text !== null && (
        <Card data-testid="diagnostic-texte">
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', font: "13px/1.5 'IBM Plex Mono', monospace" }}>{text}</pre>
          <div style={{ marginTop: 10 }}><Button size="sm" variant="quiet" onClick={() => { navigator.clipboard?.writeText(text).catch(() => {}); toast({ message: 'Diagnostic copié — rien n’a été envoyé.' }) }}>Copier le diagnostic</Button></div>
        </Card>
      )}
    </div>
  )
}

export default function AS03Contact() {
  return (
    <section data-frame="AS-03" style={{ display: 'grid', gap: 14 }}>
      <PageHeader title="Écrire au support"
        aside={<span className="t-muted" style={{ fontSize: 12 }}>réponse sous 1 jour ouvré</span>} />
      <Field label="Que se passe-t-il ?">
        {id => <Textarea id={id} placeholder="Décrivez ce que vous avez fait et ce qui s’est passé." />}
      </Field>
      <Card>
        <KV rows={[
          ['Ce qui sera joint', 'établissement, offre, campagne active, appareils enrôlés'],
          ['Ce qui ne le sera jamais', 'vos clients, leurs e-mails, le texte de vos avis'],
        ]} />
      </Card>
      <Note tone="ok">
        Un responsable du support ne peut consulter votre espace qu’avec un <strong>motif
        enregistré et une durée limitée</strong>, et vous en voyez la trace dans Paramètres.
        Personne ne regarde vos données « pour vérifier ».
      </Note>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button variant="primary">Envoyer</Button>
        <Button variant="quiet">Joindre une capture</Button>
      </div>
      <Diagnostic />
    </section>
  )
}
